// frontend/src/components/conversation/chatdag/nodes/MergeNode.tsx
import { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { usePersonaName } from '../../../../hooks/usePersonaColor';
import { CostDivergenceBadge } from '../cost/CostDivergenceBadge';
import { useOvershootMap } from '../cost/useOvershootMap';
import type { MergeNodeData } from '../types';

/**
 * MergeNode (ChatDAGRAGDAG C2.2) — the convergence node for a host-authored
 * Camp merge-back. BOTH incoming edges (main spine + camp) target the top handle
 * `t` (the emitter uses 'spine' orient for both; the camp edge's 'merge' TYPE
 * distinguishes it, not its handle); `b` continues the spine. Provenance chips
 * show the companion(s) whose work was folded in ("← <name>").
 */
function ProvenanceChip({ personaId }: { personaId: string | null }) {
  const name = usePersonaName(personaId ?? '');
  if (!personaId) return null;
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-700 dark:bg-amber-900/60 dark:text-amber-300">
      <span aria-hidden>←</span>
      <span className="truncate max-w-[90px]">{name}</span>
    </span>
  );
}

function MergeNodeImpl({ data }: { data: MergeNodeData }) {
  const mergedFrom = data.mergedFrom ?? [];
  // G4b — the context read is INTENTIONAL: it makes this memo()'d node re-render on a
  // breach upsert (the presentational flip, AC-2/AC-4). Do NOT strip/wrap it away.
  const overshoot = useOvershootMap().get(data.messageId ?? '');
  return (
    <div
      className="rounded-lg border-2 border-amber-400 bg-amber-50 px-3 py-2 shadow-sm dark:border-amber-500 dark:bg-amber-950/40"
      style={{ width: 280 }}
      data-testid={`merge-node-${data.messageId}`}
    >
      <Handle type="target" id="t" position={Position.Top} style={{ opacity: 0 }} />
      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
        <span aria-hidden>⚡</span>
        <span>folded back</span>
        <span className="ml-auto"><CostDivergenceBadge telemetry={data.costTelemetry} overshoot={overshoot} /></span>
      </div>
      <div className="mt-1 line-clamp-3 text-[11px] leading-snug text-gray-700 dark:text-gray-200">
        {data.synthPreview}
      </div>
      {mergedFrom.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {mergedFrom.map((mm) => (
            <ProvenanceChip key={mm.campId} personaId={mm.personaId} />
          ))}
        </div>
      )}
      <Handle type="source" id="b" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}

export default memo(MergeNodeImpl);
